import copy from 'copy-to-clipboard';
import { nanoid } from 'nanoid';

export function capitalizeFirstLetter(string) {
    return string ? string.charAt(0).toUpperCase() + string.slice(1) : string;
}

export function copyToClipboard(text) {
	console.log('Copy to clipboard', text);
    return copy(text);
}

export function textToColor(str) {
    let color = '#999999';
    if(str && str.length) {
        let hash = 0;
        for(let i = 0; i < str.length; i += 1) {
            hash = str.charCodeAt(i) + ((hash << 5) - hash);
        }
        color = '#';
        for(let i = 0; i < 3; i += 1) {
            const value = (hash >> (i * 8)) & 0xFF;
            color += (`00${value.toString(16)}`).substr(-2);
        }
    }
    return color;
}

export function stripTags(text) {
    return text ? text.replace(/(<([^>]+)>)/ig, '') : text;
}

// HTML 4 Latin-1 entity names in code-point order, U+00A0 to U+00FF. CKEditor emits this set by name
// and everything outside it numerically, so this table plus the numeric forms covers CMS content.
// Kept as an ordered list rather than a literal map: the index carries the code point, which makes
// the 96 entries verifiable at a glance instead of 96 hand-typed pairs to proofread.
const LATIN1_ENTITY_NAMES = ('nbsp iexcl cent pound curren yen brvbar sect uml copy ordf laquo not shy reg macr'
    + ' deg plusmn sup2 sup3 acute micro para middot cedil sup1 ordm raquo frac14 frac12 frac34 iquest'
    + ' Agrave Aacute Acirc Atilde Auml Aring AElig Ccedil Egrave Eacute Ecirc Euml Igrave Iacute Icirc Iuml'
    + ' ETH Ntilde Ograve Oacute Ocirc Otilde Ouml times Oslash Ugrave Uacute Ucirc Uuml Yacute THORN szlig'
    + ' agrave aacute acirc atilde auml aring aelig ccedil egrave eacute ecirc euml igrave iacute icirc iuml'
    + ' eth ntilde ograve oacute ocirc otilde ouml divide oslash ugrave uacute ucirc uuml yacute thorn yuml').split(' ');

const HTML_ENTITIES = LATIN1_ENTITY_NAMES.reduce((entities, name, index) => ({
    ...entities,
    [name]: String.fromCodePoint(0xA0 + index),
}), {
    lt: '<',
    gt: '>',
    quot: '"',
    apos: "'",
    // Typographic entities CKEditor inserts through its own toolbar, outside the Latin-1 range.
    hellip: '…',
    ndash: '–',
    mdash: '—',
    lsquo: '‘',
    rsquo: '’',
    ldquo: '“',
    rdquo: '”',
    bull: '•',
    euro: '€',
    trade: '™',
});

// stripTags() removes markup but leaves entities behind, so CMS-authored HTML reaches JSON-LD as
// "d&#39;origine espagnole&nbsp;qui est tr&egrave;s". Search engines and LLMs read that literally.
// Decoded without the DOM so it behaves identically during server-side rendering.
// &amp; is decoded last, on purpose: doing it earlier would turn "&amp;#39;" into an apostrophe,
// resurrecting an entity the author had deliberately escaped.
export function decodeHtmlEntities(text) {
    if(!text) {
        return text;
    }
    return `${text}`
        .replace(/&#(\d+);/g, (match, code) => String.fromCodePoint(Number(code)))
        .replace(/&#x([0-9a-f]+);/gi, (match, code) => String.fromCodePoint(parseInt(code, 16)))
        .replace(/&([a-z][a-z0-9]*);/gi, (match, name) => (HTML_ENTITIES[name] !== undefined ? HTML_ENTITIES[name] : match))
        .replace(/&amp;/g, '&');
}

// Plain-text projection of CMS rich text, for use in JSON-LD and meta tags: no markup, no entities,
// no leftover newlines or double spaces from the editor.
export function toPlainText(text) {
    return text ? decodeHtmlEntities(stripTags(text)).replace(/\s+/g, ' ').trim() : text;
}

export function truncateToChar(text, stoppingCharacter = '.') {
    return text && text.indexOf(stoppingCharacter) > 0 ? text.substring(0, text.indexOf(stoppingCharacter)) : text;
}

export function truncate(str, count = 20, splitType = 'word') {
    return splitType === 'word' ? (str || '').split(' ').splice(0, count).join(' ') : (str || '').slice(0, count);
}

export function removeAccents(string) {
    const accents = 'ÀÁÂÃÄÅĄĀāàáâãäåąßÒÓÔÕÕÖØŐòóôőõöøĎďDŽdžÈÉÊËĘèéêëęðÇçČčĆćÐÌÍÎÏĪìíîïīÙÚÛÜŰùűúûüĽĹŁľĺłÑŇŃňñńŔŕŠŚŞšśşŤťŸÝÿýŽŻŹžżźđĢĞģğ';
    const accentsOut = 'AAAAAAAAaaaaaaaasOOOOOOOOoooooooDdDZdzEEEEEeeeeeeCcCcCcDIIIIIiiiiiUUUUUuuuuuLLLlllNNNnnnRrSSSsssTtYYyyZZZzzzdGGgg';
    return `${string}`
        .split('')
        .map((letter, index) => {
            const accentIndex = accents.indexOf(letter);
            return accentIndex !== -1 ? accentsOut[accentIndex] : letter;
        })
        .join('');
}

export function getRandomString(size = 10) {
    // polyfills.js
    if(typeof window !== 'undefined' && !window.crypto) {
        window.crypto = window.msCrypto;
    }

    return nanoid(size);
}

export function nl2br(str, isXhtml) {
    if(typeof str === 'undefined' || str === null) {
        return '';
    }
    const breakTag = (isXhtml || typeof isXhtml === 'undefined') ? '<br />' : '<br>';
    return `${str}`.replace(/([^>\r\n]?)(\r\n|\n\r|\r|\n)/g, `$1${breakTag}$2`);
}
